// Single panel for the two server-derived stats (area + population).
// Fed from the area slice; loading and error states are kept compact so the
// panel doesn't push the rest of the form around.
//
// Design notes:
// - One shared font size is computed for every figure on screen, so the numbers
//   never render at mismatched sizes (the previous per-value shrink looked
//   wonky when area and population differed in length).
// - When tile billing is on, the figures are split into two visually separated
//   groups: "Billable area" (the tile-snapped values pricing uses, emphasised
//   in its own tinted block) and "Submitted boundary" (the true selection,
//   muted below). When it's off there's a single Area / Population row.
import {
  Card, CardContent, Typography, Skeleton, Box,
} from '@mui/material';

const formatArea = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const formatInt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Pick one font size that fits the longest figure in the panel, so every number
// renders at the same size. The panel lives in a ~340px column (~150px per
// number cell) and must share vertical space with the map below, so the figures
// stay deliberately compact and shrink further for long values like "1,010,572".
const valueFontSize = (texts) => {
  const len = Math.max(0, ...texts.map((t) => String(t).length));
  if (len > 11) return '0.85rem';
  if (len > 9) return '0.95rem';
  if (len > 7) return '1.05rem';
  if (len > 5) return '1.15rem';
  return '1.3rem';
};

// One metric (label + big value, with an optional unit). `emphasis` marks the
// billable figure (bold, primary text); muted is used for the submitted boundary.
const StatCell = ({
  label, value, unit, fontSize, emphasis,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="p" noWrap>{label}</Typography>
    <Typography
      component="p"
      title={unit ? `${value} ${unit}` : String(value)}
      sx={{
        fontWeight: emphasis ? 700 : 600,
        color: emphasis ? 'text.primary' : 'text.secondary',
        fontSize,
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
      {unit && (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {unit}
        </Typography>
      )}
    </Typography>
  </Box>
);

const metricGridSx = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 0.25,
};

const cardContentSx = { p: 1.25, '&:last-child': { pb: 1.25 } };

const sectionLabelSx = { lineHeight: 1.3, display: 'block', mb: 0.25 };

export const StatsPanel = ({
  areaSqMi, tileAreaSqMi = null,
  estPopulation, tileEstPopulation = null,
  loading, error, sourceLabel = 'U.S. Census 2020',
}) => {
  const tiled = tileAreaSqMi != null;
  const showBoundary = tiled
    && (Number(tileAreaSqMi) !== Number(areaSqMi)
      || Number(tileEstPopulation) !== Number(estPopulation));

  // Size every figure off the longest value currently on screen.
  const texts = tiled
    ? [
      formatArea(tileAreaSqMi), formatInt(tileEstPopulation),
      formatArea(areaSqMi), formatInt(estPopulation),
    ]
    : [formatArea(areaSqMi), formatInt(estPopulation)];
  const fontSize = valueFontSize(texts);

  return (
    <Card variant="outlined" aria-label="Area and population estimate" sx={{ flexShrink: 0 }}>
      <CardContent sx={cardContentSx}>
        {loading ? (
          <Box sx={metricGridSx}>
            <Box>
              <Typography variant="caption" color="text.secondary">Area</Typography>
              <Skeleton variant="text" width="80%" height={32} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Population</Typography>
              <Skeleton variant="text" width="80%" height={32} />
            </Box>
          </Box>
        ) : (
          <>
            {/* Billable group — when tiling is on it gets its own tinted block
                and heading; when off it's just the plain Area / Population row. */}
            <Box
              sx={tiled ? {
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 0.75,
                border: 1,
                borderColor: 'divider',
              } : undefined}
            >
              {tiled && (
                <Typography
                  variant="overline"
                  sx={{ ...sectionLabelSx, color: 'primary.main' }}
                >
                  Billable area
                </Typography>
              )}
              <Box sx={metricGridSx}>
                <StatCell
                  label="Area"
                  value={formatArea(tiled ? tileAreaSqMi : areaSqMi)}
                  unit="sq mi"
                  fontSize={fontSize}
                  emphasis
                />
                <StatCell
                  label="Population"
                  value={formatInt(tiled ? tileEstPopulation : estPopulation)}
                  fontSize={fontSize}
                  emphasis
                />
              </Box>
              {tiled && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
                  Based on the whole map tiles your area covers.
                </Typography>
              )}
            </Box>

            {showBoundary && (
              <Box sx={{ mt: 0.75, px: 0.75 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={sectionLabelSx}
                >
                  Submitted boundary
                </Typography>
                <Box sx={metricGridSx}>
                  <StatCell label="Area" value={formatArea(areaSqMi)} unit="sq mi" fontSize={fontSize} />
                  <StatCell label="Population" value={formatInt(estPopulation)} fontSize={fontSize} />
                </Box>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, px: tiled ? 0.75 : 0 }}>
              {`Est. population · ${sourceLabel}`}
            </Typography>
          </>
        )}

        {error && (
          <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>{error}</Typography>
        )}
      </CardContent>
    </Card>
  );
};
